import React, { useCallback, useEffect, useMemo, useRef } from "react"

import { useQueryClient, type QueryObserverResult } from "@tanstack/react-query"
import type { SetterOrUpdater } from "recoil"
import { useRecoilValue, useSetRecoilState, useRecoilCallback } from "recoil"

import { invalidationIntents } from "~/api/cache"
import type { UpdateProposalConsumptionDto } from "~/api/dto/proposal-consumption.dto"
import type { UpdateProposalIncentiveStatusesDto } from "~/api/dto/proposal-incentive.dto"
import type { UpdateProposalUtilitySelectionDto } from "~/api/dto/proposal-utility.dto"
import useUpdateProposalConsumptionSelection from "~/api/hooks/useUpdateProposalConsumptionSelection"
import useUpdateProposalIncentiveStatuses from "~/api/hooks/useUpdateProposalIncentiveStatuses"
import useUpdateProposalUtilitySelection from "~/api/hooks/useUpdateProposalUtilitySelection"
import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import type {
  Proposal,
  SystemType,
  UpdateProposalAdderInput,
  UpdateProposalInput,
  GetProposalQuery,
} from "~/graphql/client"
import { useUpdateProposalMutation } from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"
import { useExitPrompt } from "~/hooks/useExitPrompt"
import { createInputBuilder, get } from "~/utils/mapToInputs"
import type { Accessors } from "~/utils/mapToInputs"
import { getFrom } from "~/utils/recoil"

import { savingsRemoteState } from "../designs/design-state"
import {
  utilitySavingsRecalcBaselineUpdatedAtState,
  utilitySavingsRecalcProposalIdState,
  utilitySavingsRecalculatingState,
} from "../sync-state"
import { getSavingsUpdatedAt } from "../utility-savings-recalculating"
import { useProposalFetcher } from "./proposal-fetchers"
import { useProposalOptimisticUpdate } from "./proposal-optimistic-update"
import {
  isProposal,
  proposalDiffState,
  proposalLocalState,
  proposalRemoteState,
} from "./proposal-state"

export type ProposalActions = {
  setLocal: SetterOrUpdater<Proposal>
  changeLocal: (update: Partial<Proposal>) => void
  changeRemote: (update: Partial<Proposal>) => void
  changeAll: (update: Partial<Proposal>) => void
  save: () => Promise<void>
  reset: VoidFunction
  refetch: () =>
    | Promise<QueryObserverResult<GetProposalQuery, unknown>>
    | Promise<void>
  updateIncentives: (
    incentiveStatuses: UpdateProposalIncentiveStatusesDto[`incentiveStatuses`],
  ) => Promise<boolean>
  updatePreferredLanguage: (
    language: UpdateProposalInput[`preferredLanguage`],
  ) => Promise<boolean>
  updateSystemType: (systemType: SystemType) => Promise<boolean>
  setConsumptionInputMode: (
    mode: UpdateProposalConsumptionDto[`inputMode`],
    billEvidence?: UpdateProposalConsumptionDto[`billEvidence`],
  ) => void
  setConsumptionEntryUnits: (
    entryUnits: UpdateProposalConsumptionDto[`monthlyEnergyEntryUnits`],
  ) => void
}

export type ProposalStatus = {
  isLoading: boolean
  isSaving: boolean
  isSuccess: boolean
}

const ProposalActionsContext = React.createContext<ProposalActions | null>(null)
const ProposalStatusContext = React.createContext<ProposalStatus | null>(null)

const useProposalMachinery = (): {
  actions: ProposalActions
  status: ProposalStatus
} => {
  const { client } = useGraphqlClient()
  const setRemote = useSetRecoilState(proposalRemoteState)
  const local = useRecoilValue(proposalLocalState)
  const setLocal = useSetRecoilState(proposalLocalState)
  const { slug } = useRecoilValue(proposalRemoteState)
  const onError = useDispatchError()
  const { refetch, isLoading } = useProposalFetcher({ slug })

  // useDispatchError and useProposalFetcher (while slug is empty) return new
  // function identities per render; route them through refs so the published
  // actions keep a stable identity while always calling the freshest one.
  const onErrorRef = useRef(onError)
  const refetchRef = useRef(refetch)
  useEffect(() => {
    onErrorRef.current = onError
    refetchRef.current = refetch
  }, [onError, refetch])

  const stableRefetch = useCallback(() => refetchRef.current(), [])

  const localRef = useRef<Proposal>(local)
  const consumptionInputRef = useRef<{
    mode: UpdateProposalConsumptionDto[`inputMode`]
    billEvidence?: UpdateProposalConsumptionDto[`billEvidence`]
    entryUnits?: UpdateProposalConsumptionDto[`monthlyEnergyEntryUnits`]
  }>({ mode: `annual_usage` })
  const pendingConsumptionRequestRef = useRef<{
    fingerprint: string
    requestId: string
  } | null>(null)

  const queryClient = useQueryClient()

  const changeLocal = useCallback(
    (update: Partial<Proposal>) => {
      setLocal((local) => ({ ...local, ...update }))
    },
    [setLocal],
  )

  const changeRemote = useCallback(
    (update: Partial<Proposal>) => {
      setRemote((remote) => ({ ...remote, ...update }))
    },
    [setRemote],
  )

  const changeAll = useCallback(
    (update: Partial<Proposal>) => {
      setLocal((local) => ({ ...local, ...update }))
      setRemote((remote) => ({ ...remote, ...update }))
    },
    [setLocal, setRemote],
  )

  useProposalOptimisticUpdate(localRef)

  const getLocal = useRecoilCallback(
    ({ snapshot }) =>
      () =>
        getFrom(snapshot)(proposalLocalState),
    [],
  )

  const saveProposal = useUpdateProposalMutation(client, {
    onMutate: () => {
      localRef.current = getLocal()
    },
    onSuccess: (data) => {
      if (isProposal(data.updateProposal)) {
        setRemote(data.updateProposal)
        void invalidationIntents.proposalPricingInputsChanged(
          queryClient,
          data.updateProposal.id,
        )
      } else {
        onError(data)
      }
    },
    onError,
  })

  const saveUtilitySelection = useUpdateProposalUtilitySelection()
  const saveConsumptionSelection = useUpdateProposalConsumptionSelection()
  const saveIncentiveStatuses = useUpdateProposalIncentiveStatuses()

  const { mutateAsync: updateProposal } = saveProposal
  const { mutateAsync: updateUtilitySelection } = saveUtilitySelection
  const { mutateAsync: updateConsumptionSelection } = saveConsumptionSelection
  const { mutateAsync: updateIncentiveStatuses } = saveIncentiveStatuses

  const save = useRecoilCallback(
    ({ snapshot, set }) => {
      const getSnapshot = getFrom(snapshot)

      return async () => {
        const delta = getSnapshot(proposalDiffState)
        const { id } = getSnapshot(proposalRemoteState)
        const currentProposal = getSnapshot(proposalLocalState)

        if (delta === null) {
          console.warn(`No changes to save`)

          return
        }

        // Utility, consumption, and incentive status fields are saved through
        // dedicated REST endpoints; everything else still uses updateProposal.
        // Utility goes first so incentive eligibility reads fresh persisted rates.
        const {
          consumptionChanged,
          incentiveStatusesInput,
          utilityDelta,
          utilityInput,
          remainder,
        } = splitProposalDelta(delta)
        const updateProposalInput = createUpdateProposalInput(remainder, id)
        if (utilityInput) {
          try {
            const utilitySelection = await updateUtilitySelection({
              proposalId: id,
              data: utilityInput,
            })
            setRemote((proposal) => ({ ...proposal, ...utilityDelta }))
            if (utilitySelection.recalcQueued) {
              const baselineUpdatedAt = getSavingsUpdatedAt(
                getSnapshot(savingsRemoteState),
              )
              set(utilitySavingsRecalculatingState, true)
              set(utilitySavingsRecalcBaselineUpdatedAtState, baselineUpdatedAt)
              set(utilitySavingsRecalcProposalIdState, id)
            }
          } catch (error) {
            onErrorRef.current(error)
            throw error
          }
        }

        if (consumptionChanged) {
          const consumptionInput = createConsumptionInput(
            currentProposal,
            consumptionInputRef.current.mode,
            consumptionInputRef.current.billEvidence,
            consumptionInputRef.current.entryUnits,
          )
          const fingerprint = JSON.stringify(consumptionInput)
          const pendingRequest = pendingConsumptionRequestRef.current
          const consumptionRequest =
            pendingRequest?.fingerprint === fingerprint
              ? pendingRequest
              : { fingerprint, requestId: crypto.randomUUID() }
          pendingConsumptionRequestRef.current = consumptionRequest
          try {
            const updatedConsumption = await updateConsumptionSelection({
              proposalId: id,
              data: consumptionInput,
              requestId: consumptionRequest.requestId,
            })
            if (
              pendingConsumptionRequestRef.current?.requestId ===
              consumptionRequest.requestId
            ) {
              pendingConsumptionRequestRef.current = null
            }
            if (
              typeof updatedConsumption.annualEnergyUse === `number` &&
              Array.isArray(updatedConsumption.monthlyEnergyUse)
            ) {
              const compatibilityValues = {
                annualEnergyUse: updatedConsumption.annualEnergyUse,
                monthlyEnergyCost: currentProposal.monthlyEnergyCost,
                monthlyEnergyManualFlags:
                  currentProposal.monthlyEnergyManualFlags,
                monthlyEnergyUse: updatedConsumption.monthlyEnergyUse,
                smartEstimateBanner: currentProposal.smartEstimateBanner,
              }
              setLocal((proposal) => ({ ...proposal, ...compatibilityValues }))
              setRemote((proposal) => ({ ...proposal, ...compatibilityValues }))
            }
          } catch (error) {
            onErrorRef.current(error)
            throw error
          }
        }

        if (incentiveStatusesInput) {
          try {
            await updateIncentiveStatuses({
              proposalId: id,
              data: { incentiveStatuses: incentiveStatusesInput },
            })
            setRemote((proposal) => ({
              ...proposal,
              incentives: delta.incentives ?? proposal.incentives,
            }))
          } catch (error) {
            onErrorRef.current(error)
            throw error
          }
        }

        const hasGraphqlProposalChanges =
          Object.keys(updateProposalInput).length > 1
        if (hasGraphqlProposalChanges) {
          await updateProposal({ updateProposalInput })
        }
      }
    },
    [
      updateConsumptionSelection,
      updateIncentiveStatuses,
      updateProposal,
      updateUtilitySelection,
      setLocal,
      setRemote,
    ],
  )

  const reset = useRecoilCallback(
    ({ snapshot, set }) =>
      () => {
        pendingConsumptionRequestRef.current = null
        set(proposalLocalState, getFrom(snapshot)(proposalRemoteState))
      },
    [],
  )

  const updateIncentives = useRecoilCallback(
    ({ snapshot }) =>
      async (
        incentiveStatuses: UpdateProposalIncentiveStatusesDto[`incentiveStatuses`],
      ): Promise<boolean> => {
        const { id } = getFrom(snapshot)(proposalRemoteState)
        await updateIncentiveStatuses({
          proposalId: id,
          data: { incentiveStatuses: incentiveStatuses ?? [] },
        })

        return true
      },
    [updateIncentiveStatuses],
  )

  const updatePreferredLanguage = useRecoilCallback(
    ({ snapshot }) =>
      async (
        language: UpdateProposalInput[`preferredLanguage`],
      ): Promise<boolean> => {
        const { id } = getFrom(snapshot)(proposalRemoteState)
        await updateProposal({
          updateProposalInput: { id, preferredLanguage: language },
        })

        return true
      },
    [updateProposal],
  )

  const updateSystemType = useRecoilCallback(
    ({ snapshot, set }) =>
      async (systemType: SystemType): Promise<boolean> => {
        const { id } = getFrom(snapshot)(proposalRemoteState)
        set(proposalLocalState, (prev) => ({ ...prev, systemType }))
        set(proposalRemoteState, (prev) => ({ ...prev, systemType }))
        await updateProposal({
          updateProposalInput: { id, systemType },
        })

        return true
      },
    [updateProposal],
  )

  const setConsumptionInputMode = useCallback(
    (
      mode: UpdateProposalConsumptionDto[`inputMode`],
      billEvidence?: UpdateProposalConsumptionDto[`billEvidence`],
    ) => {
      consumptionInputRef.current = {
        ...consumptionInputRef.current,
        mode,
        billEvidence: mode === `bill` ? billEvidence : undefined,
      }
    },
    [],
  )

  const setConsumptionEntryUnits = useCallback(
    (entryUnits: UpdateProposalConsumptionDto[`monthlyEnergyEntryUnits`]) => {
      consumptionInputRef.current = {
        ...consumptionInputRef.current,
        entryUnits,
      }
    },
    [],
  )

  const actions = useMemo(
    () => ({
      setLocal,
      changeLocal,
      changeRemote,
      changeAll,
      save,
      reset,
      refetch: stableRefetch,
      updateIncentives,
      updatePreferredLanguage,
      updateSystemType,
      setConsumptionInputMode,
      setConsumptionEntryUnits,
    }),
    [
      setLocal,
      changeLocal,
      changeRemote,
      changeAll,
      save,
      reset,
      stableRefetch,
      updateIncentives,
      updatePreferredLanguage,
      updateSystemType,
      setConsumptionInputMode,
      setConsumptionEntryUnits,
    ],
  )

  const isSaving =
    saveProposal.isLoading ||
    saveConsumptionSelection.isLoading ||
    saveUtilitySelection.isLoading ||
    saveIncentiveStatuses.isLoading
  const isAnyLoading = isSaving || isLoading
  const isSuccess =
    saveProposal.isSuccess ||
    saveConsumptionSelection.isSuccess ||
    saveUtilitySelection.isSuccess ||
    saveIncentiveStatuses.isSuccess

  const status = useMemo(
    () => ({
      isLoading: isAnyLoading,
      isSaving,
      isSuccess,
    }),
    [isAnyLoading, isSaving, isSuccess],
  )

  return { actions, status }
}

export const ProposalContextProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const { actions, status } = useProposalMachinery()
  const delta = useRecoilValue(proposalDiffState)

  useExitPrompt(Boolean(delta) || status.isLoading)

  return (
    <ProposalActionsContext.Provider value={actions}>
      <ProposalStatusContext.Provider value={status}>
        {children}
      </ProposalStatusContext.Provider>
    </ProposalActionsContext.Provider>
  )
}

const rejectPublicEdit = (): never => {
  throw new Error(`Public presentations are read-only`)
}
const readOnlyProposalActions: ProposalActions = {
  setLocal: rejectPublicEdit,
  changeLocal: rejectPublicEdit,
  changeRemote: rejectPublicEdit,
  changeAll: rejectPublicEdit,
  save: rejectPublicEdit,
  reset: rejectPublicEdit,
  refetch: rejectPublicEdit,
  updateIncentives: rejectPublicEdit,
  updatePreferredLanguage: rejectPublicEdit,
  updateSystemType: rejectPublicEdit,
  setConsumptionInputMode: rejectPublicEdit,
  setConsumptionEntryUnits: rejectPublicEdit,
}
const readOnlyProposalStatus: ProposalStatus = {
  isLoading: false,
  isSaving: false,
  isSuccess: true,
}
export const ReadOnlyProposalContextProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => (
  <ProposalActionsContext.Provider value={readOnlyProposalActions}>
    <ProposalStatusContext.Provider value={readOnlyProposalStatus}>
      {children}
    </ProposalStatusContext.Provider>
  </ProposalActionsContext.Provider>
)

export const useProposalActions = (): ProposalActions => {
  const actions = React.useContext(ProposalActionsContext)

  if (!actions) {
    throw new Error(`Proposal context not found`)
  }

  return actions
}

export const useProposalStatus = (): ProposalStatus => {
  const status = React.useContext(ProposalStatusContext)

  if (!status) {
    throw new Error(`Proposal context not found`)
  }

  return status
}

export const useProposalContext = (): ReturnType<UseProposal> => {
  const actions = useProposalActions()
  const status = useProposalStatus()
  const local = useRecoilValue(proposalLocalState)
  const remote = useRecoilValue(proposalRemoteState)
  const delta = useRecoilValue(proposalDiffState)

  return useMemo(
    () => ({
      ...actions,
      ...status,
      local,
      remote,
      delta,
      isDirty: Boolean(delta),
    }),
    [actions, status, local, remote, delta],
  )
}

export type UseProposal = () => {
  remote: Proposal
  local: Proposal
  setLocal: SetterOrUpdater<Proposal>
  save: VoidFunction | (() => Promise<void>)
  changeLocal: (update: Partial<Proposal>) => void
  changeRemote: (update: Partial<Proposal>) => void
  changeAll: (update: Partial<Proposal>) => void
  reset: VoidFunction
  isDirty: boolean
  refetch: () =>
    | Promise<QueryObserverResult<GetProposalQuery, unknown>>
    | Promise<void>
  delta: Partial<Proposal> | null
  updateIncentives: (
    incentiveStatuses: UpdateProposalIncentiveStatusesDto[`incentiveStatuses`],
  ) => Promise<boolean>
  updatePreferredLanguage: (
    language: UpdateProposalInput[`preferredLanguage`],
  ) => Promise<boolean>
  updateSystemType: (systemType: SystemType) => Promise<boolean>
  setConsumptionInputMode: (
    mode: UpdateProposalConsumptionDto[`inputMode`],
    billEvidence?: UpdateProposalConsumptionDto[`billEvidence`],
  ) => void
  setConsumptionEntryUnits: (
    entryUnits: UpdateProposalConsumptionDto[`monthlyEnergyEntryUnits`],
  ) => void
  isLoading: boolean
  isSuccess: boolean
  isSaving: boolean
}

/**
 * Splits a proposal delta into fields handled by REST endpoints and everything
 * else (still saved via the GraphQL updateProposal mutation). Exported for
 * tests.
 */
export const splitProposalDelta = (
  delta: Partial<Proposal>,
): {
  incentiveStatusesInput:
    | UpdateProposalIncentiveStatusesDto[`incentiveStatuses`]
    | null
  utilityInput: UpdateProposalUtilitySelectionDto | null
  utilityDelta: Partial<Proposal>
  consumptionChanged: boolean
  remainder: Partial<Proposal>
} => {
  const {
    annualEnergyUse,
    incentives,
    monthlyEnergyCost,
    monthlyEnergyManualFlags,
    monthlyEnergyUse,
    smartEstimateBanner,
    utilityRate,
    postSolarUtilityRate,
    utilityRateEscalationPercentage,
    userAdjustedUtilityRate,
    userAdjustedBeforeSolarContractedRate,
    userAdjustedAfterSolarContractedRate,
    ...remainingFields
  } = delta

  const consumptionChanged = [
    annualEnergyUse,
    monthlyEnergyCost,
    monthlyEnergyManualFlags,
    monthlyEnergyUse,
  ].some((value) => value !== undefined)
  const remainder = {
    ...remainingFields,
    ...(!consumptionChanged && smartEstimateBanner !== undefined
      ? { smartEstimateBanner }
      : {}),
  }

  const utilityInput: UpdateProposalUtilitySelectionDto = {}
  const utilityDelta: Partial<Proposal> = {
    ...(utilityRate !== undefined ? { utilityRate } : {}),
    ...(postSolarUtilityRate !== undefined ? { postSolarUtilityRate } : {}),
    ...(utilityRateEscalationPercentage !== undefined
      ? { utilityRateEscalationPercentage }
      : {}),
    ...(userAdjustedUtilityRate !== undefined
      ? { userAdjustedUtilityRate }
      : {}),
    ...(userAdjustedBeforeSolarContractedRate !== undefined
      ? { userAdjustedBeforeSolarContractedRate }
      : {}),
    ...(userAdjustedAfterSolarContractedRate !== undefined
      ? { userAdjustedAfterSolarContractedRate }
      : {}),
  }

  if (utilityRate?.id) {
    utilityInput.utilityRateId = utilityRate.id
  }
  if (postSolarUtilityRate?.id) {
    utilityInput.postSolarInstallUtilityRateId = postSolarUtilityRate.id
  }
  if (typeof utilityRateEscalationPercentage === `number`) {
    utilityInput.utilityRateEscalationPercentage =
      utilityRateEscalationPercentage
  }
  // null is meaningful for the user-adjusted rates: it resets the override
  // back to the Genability default. Only undefined means "not touched".
  if (userAdjustedUtilityRate !== undefined) {
    utilityInput.userAdjustedUtilityRate = userAdjustedUtilityRate
  }
  if (userAdjustedBeforeSolarContractedRate !== undefined) {
    utilityInput.userAdjustedBeforeSolarContractedRate =
      userAdjustedBeforeSolarContractedRate
  }
  if (userAdjustedAfterSolarContractedRate !== undefined) {
    utilityInput.userAdjustedAfterSolarContractedRate =
      userAdjustedAfterSolarContractedRate
  }

  return {
    consumptionChanged,
    incentiveStatusesInput: incentives
      ? incentives.map(
          ({ incentiveId, proposalId, showOnBrochure, applyToLoan }) => ({
            incentiveId,
            proposalId,
            showOnBrochure,
            applyToLoan,
          }),
        )
      : null,
    utilityInput: Object.keys(utilityInput).length > 0 ? utilityInput : null,
    utilityDelta,
    remainder,
  }
}

const createConsumptionInput = (
  proposal: Proposal,
  inputMode: UpdateProposalConsumptionDto[`inputMode`],
  billEvidence?: UpdateProposalConsumptionDto[`billEvidence`],
  entryUnits?: UpdateProposalConsumptionDto[`monthlyEnergyEntryUnits`],
): UpdateProposalConsumptionDto => {
  if (
    proposal.monthlyEnergyUse.length !== 12 ||
    proposal.monthlyEnergyUse.some((value) => !Number.isFinite(value))
  ) {
    throw new Error(
      `Twelve monthly energy-use values are required to save consumption`,
    )
  }
  if (
    proposal.monthlyEnergyCost.length !== 12 ||
    proposal.monthlyEnergyCost.some((value) => !Number.isFinite(value))
  ) {
    throw new Error(
      `Twelve monthly energy-cost values are required to save consumption`,
    )
  }
  if (proposal.monthlyEnergyManualFlags.length !== 12) {
    throw new Error(
      `Twelve monthly entry flags are required to save consumption`,
    )
  }

  const annualEnergyUse =
    inputMode === `annual_usage`
      ? proposal.annualEnergyUse
      : proposal.monthlyEnergyUse.reduce((total, value) => total + value, 0)

  if (!annualEnergyUse || annualEnergyUse <= 0) {
    throw new Error(`Annual energy use is required to save consumption`)
  }

  return {
    inputMode,
    annualEnergyUse,
    monthlyEnergyUse: proposal.monthlyEnergyUse,
    monthlyEnergyCost: proposal.monthlyEnergyCost,
    monthlyEnergyManualFlags: proposal.monthlyEnergyManualFlags,
    smartEstimateBanner: proposal.smartEstimateBanner,
    ...(inputMode === `bill` && billEvidence?.bills.length
      ? { billEvidence }
      : {}),
    ...(entryUnits?.length === 12
      ? { monthlyEnergyEntryUnits: entryUnits }
      : {}),
  }
}

const updateProposalInputAccessors: Accessors<Proposal, UpdateProposalInput> = {
  id: get,
  systemType: get,
  downPayment: get,
  proposalAdders: ({ proposalAdders }) =>
    proposalAdders?.map<UpdateProposalAdderInput>(
      ({
        powurOsAdderId,
        proposalId,
        selected,
        customPrice,
        customQuantity,
      }) => ({
        powurOsAdderId,
        proposalId,
        selected,
        customPrice,
        customQuantity,
      }),
    ),
  margin: get,
  selectedLoanProductId: (proposal) =>
    proposal.selectedLoanProduct?.id ?? undefined,
  preferredLanguage: get,
  financeType: get,
  fundingGroup: get,
  smartEstimateBanner: get,
  newCustomerIncentive: get,
}

const createUpdateProposalInput = createInputBuilder<
  Proposal,
  UpdateProposalInput
>(updateProposalInputAccessors)
